// src/features/bom/components/TransferToFloorModal.tsx

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import {
    createFloorTransfer,
    ProductionFloor,
} from '../../services/productionFloorApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import { searchProducts, ProductResult } from '@/features/pos/services/posApi';
import {
    X, Truck, Search, Plus, Trash2, Package,
    Building2, ArrowRight, CheckCircle, AlertCircle,
    ChevronDown, ChevronUp, GripVertical, Layers,
    ClipboardList, Zap, Minus, Hash,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface TransferItem {
    productId: string;
    productName: string;
    productSku?: string;
    quantity: string;
    uomId: string;
    uomName: string;
    costPrice?: string;
}

interface TransferToFloorModalProps {
    isOpen: boolean;
    onClose: () => void;
    floor: ProductionFloor;
}

const QUICK_QUANTITIES = ['0.5', '1', '2', '5', '10', '25', '50', '100'];

export function TransferToFloorModal({ isOpen, onClose, floor }: TransferToFloorModalProps) {
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Source
    const [sourceType, setSourceType] = useState<'branch' | 'production_floor'>('branch');
    const [sourceId, setSourceId] = useState('');

    // Items
    const [items, setItems] = useState<TransferItem[]>([]);
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Product search
    const [productSearch, setProductSearch] = useState('');
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
    const productDropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Batch quantity mode
    const [batchQuantity, setBatchQuantity] = useState('1');
    const [showBatchMode, setShowBatchMode] = useState(false);

    // Expanded item
    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

    // Fetch branches
    const { data: branches } = useQuery({
        queryKey: ['branches-transfer', user?.tenantId],
        queryFn: () => getBranches(user!.tenantId),
        enabled: isOpen && !!user?.tenantId,
        staleTime: 5 * 60 * 1000,
    });

    // Debounced product search
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(productSearch), 300);
        return () => clearTimeout(timer);
    }, [productSearch]);

    const { data: productResults, isLoading: productsLoading } = useQuery({
        queryKey: ['transfer-product-search', user?.tenantId, debouncedSearch],
        queryFn: () => searchProducts(user!.tenantId, debouncedSearch),
        enabled: !!user?.tenantId && debouncedSearch.length >= 2,
        staleTime: 30 * 1000,
    });

    // Close dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (productDropdownRef.current && !productDropdownRef.current.contains(e.target as Node)) {
                setShowProductDropdown(false);
                setSelectedProductIndex(-1);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Auto-select source branch
    useEffect(() => {
        if (isOpen && branches && branches.length > 0 && !sourceId) {
            setSourceId(branches[0].id);
        }
    }, [isOpen, branches]);

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showProductDropdown || !productResults?.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedProductIndex(prev => Math.min(prev + 1, productResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedProductIndex(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter' && selectedProductIndex >= 0) {
            e.preventDefault();
            addItem(productResults[selectedProductIndex]);
        } else if (e.key === 'Escape') {
            setShowProductDropdown(false);
            setSelectedProductIndex(-1);
        }
    };

    const addItem = useCallback((product: ProductResult) => {
        if (items.find(i => i.productId === product.id)) {
            addToast({ type: 'warning', title: 'Already added', message: `${product.name} is already in the transfer list.` });
            return;
        }

        const baseUom = product.uoms?.find(u => u.isBaseUom) || product.uoms?.[0];
        setItems(prev => [...prev, {
            productId: product.id,
            productName: product.name || 'Unknown',
            productSku: product.sku || undefined,
            quantity: showBatchMode ? batchQuantity : '1',
            uomId: baseUom?.id || '',
            uomName: baseUom?.abbreviation || baseUom?.name || 'unit',
            costPrice: product.costPrice || undefined,
        }]);
        setProductSearch('');
        setShowProductDropdown(false);
        setSelectedProductIndex(-1);
        searchInputRef.current?.focus();
    }, [items, showBatchMode, batchQuantity, addToast]);

    const removeItem = (index: number) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    const updateItemQuantity = (index: number, quantity: string) => {
        setItems(prev => {
            const newItems = [...prev];
            newItems[index] = { ...newItems[index], quantity };
            return newItems;
        });
    };

    const setAllQuantities = (qty: string) => {
        setItems(prev => prev.map(item => ({ ...item, quantity: qty })));
    };

    const clearAllItems = () => {
        if (items.length > 0 && confirm(`Remove all ${items.length} items from the transfer list?`)) {
            setItems([]);
        }
    };

    // Stats
    const totalQuantity = useMemo(() =>
        items.reduce((sum, i) => sum + (parseFloat(i.quantity) || 0), 0),
        [items]);
    const totalCost = useMemo(() =>
        items.reduce((sum, i) => sum + (parseFloat(i.quantity) || 0) * (parseFloat(i.costPrice || '0') || 0), 0),
        [items]);

    const handleSubmit = async () => {
        if (!sourceId) {
            addToast({ type: 'error', title: 'Source Required', message: 'Select a source branch or floor.' });
            return;
        }
        if (items.length === 0) {
            addToast({ type: 'error', title: 'No Items', message: 'Add at least one product to transfer.' });
            return;
        }
        if (items.some(i => !i.quantity || parseFloat(i.quantity) <= 0)) {
            addToast({ type: 'error', title: 'Invalid Quantity', message: 'All items must have a quantity greater than 0.' });
            return;
        }

        setIsSubmitting(true);
        try {
            await createFloorTransfer(user!.tenantId, {
                sourceType,
                sourceId,
                destinationType: 'production_floor',
                destinationId: floor.id,
                items: items.map(i => ({
                    productId: i.productId,
                    quantity: i.quantity,
                    uomId: i.uomId,
                })),
                notes: notes || undefined,
            });

            queryClient.invalidateQueries({ queryKey: ['floor-inventory', floor.id] });
            queryClient.invalidateQueries({ queryKey: ['floor-transfers', floor.id] });
            queryClient.invalidateQueries({ queryKey: ['floor-dashboard', floor.id] });

            addToast({
                type: 'success',
                title: 'Transfer Complete!',
                message: `${items.length} item${items.length > 1 ? 's' : ''} (${totalQuantity.toLocaleString()} total) transferred to ${floor.name}.`
            });
            onClose();
        } catch (err: any) {
            addToast({ type: 'error', title: 'Transfer Failed', message: err?.message || 'Could not complete transfer.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setSourceType('branch');
        setSourceId('');
        setItems([]);
        setNotes('');
        setProductSearch('');
        setShowBatchMode(false);
        setBatchQuantity('1');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const filteredProducts = useMemo(() => {
        if (!productResults) return [];
        return productResults.filter(p => !items.find(i => i.productId === p.id));
    }, [productResults, items]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={handleClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4 rounded-t-3xl flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3 text-white">
                                <Truck className="w-5 h-5" />
                                <div>
                                    <h2 className="text-lg font-bold">Transfer Stock to Floor</h2>
                                    <p className="text-xs text-white/80">Destination: {floor.name}</p>
                                </div>
                            </div>
                            <button onClick={handleClose} className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors" type="button">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto">
                            <div className="p-6 space-y-5">
                                {/* Source Selection */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                        Source Location
                                    </label>
                                    <div className="flex items-center gap-2 mb-3">
                                        <button
                                            onClick={() => { setSourceType('branch'); setSourceId(''); }}
                                            className={clsx(
                                                'flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2',
                                                sourceType === 'branch'
                                                    ? 'bg-amber-100 text-amber-700 border-2 border-amber-300'
                                                    : 'bg-slate-50 text-slate-500 border-2 border-slate-200 hover:border-slate-300'
                                            )}
                                            type="button"
                                        >
                                            <Building2 className="w-4 h-4" /> Branch
                                        </button>
                                        <button
                                            onClick={() => { setSourceType('production_floor'); setSourceId(''); }}
                                            className={clsx(
                                                'flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2',
                                                sourceType === 'production_floor'
                                                    ? 'bg-amber-100 text-amber-700 border-2 border-amber-300'
                                                    : 'bg-slate-50 text-slate-500 border-2 border-slate-200 hover:border-slate-300'
                                            )}
                                            type="button"
                                        >
                                            <Layers className="w-4 h-4" /> Prod. Floor
                                        </button>
                                    </div>
                                    <select
                                        value={sourceId}
                                        onChange={(e) => setSourceId(e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
                                    >
                                        <option value="">Select source...</option>
                                        {(branches || []).map((b: any) => (
                                            <option key={b.id} value={b.id}>{b.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Product Search */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                            Add Products
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={showBatchMode}
                                                    onChange={(e) => setShowBatchMode(e.target.checked)}
                                                    className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                                />
                                                Batch quantity
                                            </label>
                                            {showBatchMode && (
                                                <input
                                                    type="number"
                                                    step="0.001"
                                                    min="0.001"
                                                    value={batchQuantity}
                                                    onChange={(e) => setBatchQuantity(e.target.value)}
                                                    className="w-20 px-2 py-1 border border-slate-200 rounded-lg text-xs text-center"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="relative" ref={productDropdownRef}>
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            placeholder="Search products by name, SKU, or barcode..."
                                            value={productSearch}
                                            onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); setSelectedProductIndex(-1); }}
                                            onFocus={() => { if (productResults?.length) setShowProductDropdown(true); }}
                                            onKeyDown={handleKeyDown}
                                            className="w-full pl-10 pr-10 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
                                        />
                                        {productsLoading && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <Spinner size="xs" />
                                            </div>
                                        )}
                                        <AnimatePresence>
                                            {showProductDropdown && filteredProducts.length > 0 && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -4 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -4 }}
                                                    className="absolute top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-64 overflow-y-auto"
                                                >
                                                    {filteredProducts.slice(0, 50).map((p, idx) => (
                                                        <button
                                                            key={p.id}
                                                            type="button"
                                                            onClick={() => addItem(p)}
                                                            onMouseEnter={() => setSelectedProductIndex(idx)}
                                                            className={clsx(
                                                                'w-full text-left px-4 py-3 text-sm border-b border-slate-50 last:border-0 flex items-center justify-between transition-colors',
                                                                selectedProductIndex === idx ? 'bg-amber-50' : 'hover:bg-slate-50'
                                                            )}
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-slate-700 truncate">{p.name}</p>
                                                                <p className="text-xs text-slate-400 font-mono">{p.sku || p.barcode || 'No code'}</p>
                                                            </div>
                                                            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                                                {p.costPrice && (
                                                                    <span className="text-xs text-slate-400">₦{parseFloat(p.costPrice).toLocaleString()}</span>
                                                                )}
                                                                <Plus className="w-4 h-4 text-amber-500" />
                                                            </div>
                                                        </button>
                                                    ))}
                                                    {filteredProducts.length > 50 && (
                                                        <div className="px-4 py-2 text-xs text-slate-400 text-center bg-slate-50">
                                                            Showing 50 of {filteredProducts.length} results. Refine your search.
                                                        </div>
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                {/* Items List */}
                                {items.length > 0 && (
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                                Transfer Items ({items.length})
                                            </label>
                                            <div className="flex items-center gap-2">
                                                {items.length > 1 && (
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-[10px] text-slate-400">Set all to:</span>
                                                        <select
                                                            onChange={(e) => { if (e.target.value) setAllQuantities(e.target.value); }}
                                                            className="px-2 py-1 border border-slate-200 rounded-lg text-[10px] bg-white"
                                                            defaultValue=""
                                                        >
                                                            <option value="" disabled>Qty</option>
                                                            {QUICK_QUANTITIES.map(q => (
                                                                <option key={q} value={q}>{q}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={clearAllItems}
                                                    className="text-[10px] text-rose-500 hover:text-rose-600 font-medium"
                                                    type="button"
                                                >
                                                    Clear all
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                            {items.map((item, idx) => (
                                                <motion.div
                                                    key={item.productId}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: idx * 0.02 }}
                                                    className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden"
                                                >
                                                    <div className="flex items-center gap-3 p-3">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <Package className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                                <p className="text-sm font-medium text-slate-700 truncate">{item.productName}</p>
                                                            </div>
                                                            <div className="flex items-center gap-2 mt-0.5 ml-6">
                                                                {item.productSku && (
                                                                    <span className="text-xs text-slate-400 font-mono">{item.productSku}</span>
                                                                )}
                                                                <span className="text-xs text-slate-400">{item.uomName}</span>
                                                                {item.costPrice && (
                                                                    <span className="text-xs text-slate-400">· ₦{parseFloat(item.costPrice).toLocaleString()}/unit</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden">
                                                                <button
                                                                    onClick={() => {
                                                                        const current = parseFloat(item.quantity) || 0;
                                                                        updateItemQuantity(idx, Math.max(0.001, current - 1).toString());
                                                                    }}
                                                                    className="p-1.5 hover:bg-slate-200 text-slate-500 transition-colors"
                                                                    type="button"
                                                                >
                                                                    <Minus size={12} />
                                                                </button>
                                                                <input
                                                                    type="number"
                                                                    step="0.001"
                                                                    min="0.001"
                                                                    value={item.quantity}
                                                                    onChange={(e) => updateItemQuantity(idx, e.target.value)}
                                                                    className="w-20 px-2 py-1.5 text-sm text-center border-x border-slate-300 bg-white focus:outline-none"
                                                                />
                                                                <button
                                                                    onClick={() => {
                                                                        const current = parseFloat(item.quantity) || 0;
                                                                        updateItemQuantity(idx, (current + 1).toString());
                                                                    }}
                                                                    className="p-1.5 hover:bg-slate-200 text-slate-500 transition-colors"
                                                                    type="button"
                                                                >
                                                                    <Plus size={12} />
                                                                </button>
                                                            </div>
                                                            <span className="text-xs font-semibold text-slate-500 w-16 text-right tabular-nums">
                                                                {item.costPrice
                                                                    ? `₦${((parseFloat(item.quantity) || 0) * parseFloat(item.costPrice)).toLocaleString()}`
                                                                    : `${(parseFloat(item.quantity) || 0).toLocaleString()} ${item.uomName}`}
                                                            </span>
                                                            <button
                                                                onClick={() => removeItem(idx)}
                                                                className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                                                                type="button"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {/* Quick quantity chips */}
                                                    <div className="px-3 pb-2 ml-9 flex items-center gap-1 flex-wrap">
                                                        {QUICK_QUANTITIES.slice(0, 6).map(q => (
                                                            <button
                                                                key={q}
                                                                onClick={() => updateItemQuantity(idx, q)}
                                                                className={clsx(
                                                                    'px-2 py-0.5 text-[10px] rounded-md font-medium transition-colors',
                                                                    item.quantity === q
                                                                        ? 'bg-amber-100 text-amber-700'
                                                                        : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-100'
                                                                )}
                                                                type="button"
                                                            >
                                                                {q}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Summary */}
                                {items.length > 0 && (
                                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-4">
                                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-3">Transfer Summary</p>
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div className="bg-white/80 rounded-xl p-3">
                                                <p className="text-[10px] text-slate-400 uppercase font-semibold">Items</p>
                                                <p className="text-xl font-black text-slate-800">{items.length}</p>
                                            </div>
                                            <div className="bg-white/80 rounded-xl p-3">
                                                <p className="text-[10px] text-slate-400 uppercase font-semibold">Total Quantity</p>
                                                <p className="text-xl font-black text-slate-800">{totalQuantity.toLocaleString()}</p>
                                            </div>
                                            <div className="bg-white/80 rounded-xl p-3">
                                                <p className="text-[10px] text-slate-400 uppercase font-semibold">Est. Value</p>
                                                <p className="text-xl font-black text-slate-800">₦{totalCost.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notes */}
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                        Notes (Optional)
                                    </label>
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        rows={2}
                                        placeholder="e.g., Restocking for morning shift, weekly supply..."
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-white rounded-b-3xl">
                            <button
                                onClick={handleClose}
                                className="px-5 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
                                type="button"
                            >
                                Cancel
                            </button>
                            <div className="flex items-center gap-3">
                                {items.length > 0 && (
                                    <span className="text-xs text-slate-400 hidden sm:block">
                                        {items.length} items · {totalQuantity.toLocaleString()} total
                                    </span>
                                )}
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || !sourceId || items.length === 0}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold text-sm shadow-lg hover:from-amber-600 hover:to-orange-700 disabled:opacity-50 transition-all"
                                    type="button"
                                >
                                    {isSubmitting ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        <Truck className="w-4 h-4" />
                                    )}
                                    Complete Transfer
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}